const DEFAULT_CTA_LABEL = "Take me to Korero";

export function buildDefaultCtaLabel(productName: string | null | undefined): string {
  const name = productName?.trim();
  return name ? `Take me to ${name}` : DEFAULT_CTA_LABEL;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeHtmlAttr(text: string): string {
  return escapeHtml(text).replace(/'/g, "&#39;");
}

/** Email-safe CTA markup stored in the database and sent to recipients. */
export function buildEmailCtaHtml(label: string): string {
  const safeLabel = escapeHtml(label.trim() || DEFAULT_CTA_LABEL);
  const safeAttr = escapeHtmlAttr(label.trim() || DEFAULT_CTA_LABEL);

  return `<div class="korero-email-cta" data-korero-cta="true" data-label="${safeAttr}">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:16px auto 0 auto;">
    <tr>
      <td align="center">
        <a href="{{CLIENT_HOME_URL}}" style="display:inline-block;background-color:#PRIMARY_COLOUR#;color:#ffffff !important;text-decoration:none;font-weight:600;font-size:14px;line-height:1;padding:11px 28px;border-radius:8px;min-width:180px;text-align:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;">${safeLabel}</a>
      </td>
    </tr>
  </table>
</div>`;
}

/** Editor-only CTA blot markup (includes the "Click to edit label" hint). */
export function buildEditorCtaBlotHtml(label: string): string {
  const safeLabel = escapeHtml(label.trim() || DEFAULT_CTA_LABEL);
  const safeAttr = escapeHtmlAttr(label.trim() || DEFAULT_CTA_LABEL);

  return `<div class="ql-korero-cta-blot" data-label="${safeAttr}" contenteditable="false">
        <div class="korero-cta-inner">
          <a class="korero-cta-button bg-primary" href="javascript:void(0)">
            <span class="korero-cta-label">${safeLabel}</span>
          </a>
          <span class="korero-cta-hint">Click to edit label</span>
        </div>
      </div>`;
}

function readCtaLabelFromElement(el: Element): string {
  const fromAttr = el.getAttribute("data-label")?.trim();
  if (fromAttr) {
    return fromAttr;
  }

  const fromSpan = el.querySelector(".korero-cta-label")?.textContent?.trim();
  if (fromSpan) {
    return fromSpan;
  }

  const fromAnchor = el.querySelector("a")?.textContent?.trim();
  if (fromAnchor) {
    return fromAnchor;
  }

  return DEFAULT_CTA_LABEL;
}

/** True for Quill spacer paragraphs: empty, whitespace-only, or only br children. */
function isSpacerParagraph(p: Element): boolean {
  const hasNonBrElement = Array.from(p.children).some((el) => el.tagName !== "BR");
  if (hasNonBrElement) {
    return false;
  }

  const text = (p.textContent ?? "").replace(/\u00a0/g, " ").trim();
  return text.length === 0;
}

function stripSpacerParagraphs(root: ParentNode): void {
  root.querySelectorAll("p").forEach((p) => {
    if (isSpacerParagraph(p)) {
      p.remove();
    }
  });
}

/** Convert editor DOM HTML to the persisted / email-ready format. */
export function serializeEditorHtmlForStorage(html: string): string {
  if (!html) {
    return html;
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  div.querySelectorAll(".ql-korero-cta-blot").forEach((el) => {
    const label = readCtaLabelFromElement(el);
    const wrapper = document.createElement("div");
    wrapper.innerHTML = buildEmailCtaHtml(label);
    const replacement = wrapper.firstElementChild;
    if (replacement) {
      el.replaceWith(replacement);
    }
  });

  div.querySelectorAll(".korero-cta-hint").forEach((el) => el.remove());

  stripSpacerParagraphs(div);

  return div.innerHTML;
}

/** True when message HTML includes a Korero CTA block (stored or editor format). */
export function hasMessageCta(html: string): boolean {
  if (!html?.trim()) {
    return false;
  }

  const div = document.createElement("div");
  div.innerHTML = html;
  return !!div.querySelector(
    '[data-korero-cta="true"], .korero-email-cta, .ql-korero-cta-blot',
  );
}

/**
 * Split stored or editor message HTML into body content and CTA block.
 * CTA is always returned in email/storage format for persistence.
 */
export function splitMessageHtmlAroundCta(html: string): {
  bodyHtml: string;
  ctaHtml: string | null;
} {
  if (!html?.trim()) {
    return { bodyHtml: "", ctaHtml: null };
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  const ctaEl = div.querySelector(
    '[data-korero-cta="true"], .korero-email-cta, .ql-korero-cta-blot',
  );
  if (!ctaEl) {
    return { bodyHtml: html.trim(), ctaHtml: null };
  }

  const ctaHtml = ctaEl.classList.contains("ql-korero-cta-blot")
    ? buildEmailCtaHtml(readCtaLabelFromElement(ctaEl))
    : ctaEl.outerHTML;

  ctaEl.remove();
  div.querySelectorAll(".korero-cta-hint").forEach((el) => el.remove());
  stripSpacerParagraphs(div);

  return { bodyHtml: div.innerHTML.trim(), ctaHtml };
}

/** Replace body content while keeping an existing CTA block unchanged. */
export function mergeMessageBodyWithCta(
  bodyHtml: string,
  ctaHtml: string | null,
): string {
  if (!ctaHtml) {
    return bodyHtml;
  }

  const trimmedBody = bodyHtml.trim();
  if (!trimmedBody) {
    return ctaHtml;
  }

  return `${trimmedBody}${ctaHtml}`;
}

/** Convert stored / email HTML back into editor blot markup when loading the form. */
export function deserializeStorageHtmlForEditor(html: string): string {
  if (!html) {
    return html;
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  div
    .querySelectorAll('[data-korero-cta="true"], .korero-email-cta')
    .forEach((el) => {
      const label = readCtaLabelFromElement(el);
      const wrapper = document.createElement("div");
      wrapper.innerHTML = buildEditorCtaBlotHtml(label);
      const replacement = wrapper.firstElementChild;
      if (replacement) {
        el.replaceWith(replacement);
      }
    });

  return div.innerHTML;
}
